import { type Flags, useFlag } from '@reflag/react-sdk';
import { useAtom } from 'jotai';
import { atomFamily } from 'jotai-family';
import { atomWithStorage } from 'jotai/utils';
import React from 'react';

import { Badge } from '@gitbook/spine-react';
import { useNow } from '@gitbook/util-react-hooks';

/**
 * Atom to track when a feature was first seen by a user.
 */
const featuresFirstSeenAtom = atomFamily((featureKey: string) =>
    atomWithStorage<number | null>(
        `featuresFirstSeenAtom__${JSON.stringify(featureKey)}`,
        null,
        undefined,
        { getOnInit: true }
    )
);

type NewFeatureBadgeProps = {
    /**
     * ID of the feature to indicate as new.
     */
    flagKey: keyof Flags;

    /**
     * How long should the badge be displayed for after the user first sees it? In miliseconds.
     * @default 1 week
     */
    showFor?: number;
};

/**
 * Badge to indicate a new feature.
 */
export function NewFeatureBadge(props: NewFeatureBadgeProps) {
    const { flagKey } = props;
    const feature = useFlag(flagKey);

    if (!feature.isEnabled) {
        return null;
    }

    return <NewFeatureBadgeEnabled {...props} />;
}

function NewFeatureBadgeEnabled(props: NewFeatureBadgeProps) {
    const { flagKey: featureKey, showFor = 1000 * 60 * 60 * 24 * 7 } = props;
    const [firstSeen, setFirstSeen] = useAtom(featuresFirstSeenAtom(featureKey));
    const now = useNow();

    const isNew = firstSeen === null || now - firstSeen < showFor;

    // Set the first seen timestamp when the feature is first seen.
    React.useEffect(() => {
        if (firstSeen === null) {
            setFirstSeen(Date.now());
        }
    }, [firstSeen, setFirstSeen]);

    if (!isNew) {
        return null;
    }

    return <Badge kind="new" size="xsmall" label="New" />;
}
